import {
  IMAGE_UNSUPPORTED_MESSAGE,
  MAX_IMAGE_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_LABEL,
  readFileAsDataUri,
  validateImageFile,
} from "./imageUpload";

// The profile picture shown on the booking page. Everything that reads or
// writes it goes through here so the builder, the visual editor and the public
// page agree on what counts as a picture and what is sent to the server.

export const AVATAR_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
export const AVATAR_MAX_IMAGE_BYTES = MAX_IMAGE_UPLOAD_BYTES;
export const AVATAR_MAX_IMAGE_SIZE_LABEL = MAX_IMAGE_UPLOAD_LABEL;

// The booking page draws the picture as a disc of at most 120 CSS pixels, so a
// 512px square still covers a 3x screen. Cropping to a square here is what
// makes the disc show the middle of the photo rather than a stretched strip.
export const AVATAR_OUTPUT_SIZE = 512;

const JPEG_QUALITY = 0.9;

export const resolveAvatarUrl = (image) => {
  if (typeof image === "string") return image.trim();
  if (typeof image?.url === "string") return image.url.trim();
  return "";
};

// The same letter the public page falls back to, so the builder's preview and
// the live page can never disagree about which one it is.
export const getAvatarInitial = (...candidates) => {
  for (const candidate of candidates) {
    const text = typeof candidate === "string" ? candidate.trim() : "";
    if (text) return text.charAt(0).toUpperCase();
  }
  return "";
};

export const validateAvatarFile = (file) => {
  const message = validateImageFile(file, { acceptedTypes: AVATAR_ACCEPT.split(",") });
  return message === IMAGE_UNSUPPORTED_MESSAGE
    ? "Unsupported image type. Use a PNG, JPG or WEBP file."
    : message;
};

// A decode that never resolves would leave the button stuck on "Uploading...",
// so the wait is bounded and a timeout falls back to the original file.
const IMAGE_DECODE_TIMEOUT_MS = 10000;

const loadImageElement = (dataUri) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const timeoutId = setTimeout(
      () => reject(new Error("Timed out reading image.")),
      IMAGE_DECODE_TIMEOUT_MS,
    );
    const settle = (handler) => (value) => {
      clearTimeout(timeoutId);
      handler(value);
    };
    image.onload = settle(() => resolve(image));
    image.onerror = settle(() => reject(new Error("We couldn't read that image. Try a different file.")));
    image.src = dataUri;
  });

// Checked before the image is decoded, not after: a canvas we cannot draw on
// makes the whole resize pointless, and there is no reason to wait on a decode
// first.
const createSquareCanvasContext = (size) => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext?.("2d");
    return context ? { canvas, context } : null;
  } catch (error) {
    return null;
  }
};

/* Square-crops and downscales in the browser before upload. A phone photo is
   several megabytes of pixels nobody will ever see at this size, and the crop
   has to happen somewhere: doing it here means the owner's saved picture is
   the same one the preview showed them. A browser without a usable canvas
   falls back to the original file, which the server still accepts. */
export const prepareAvatarUpload = async (file) => {
  const originalDataUri = await readFileAsDataUri(file);

  if (!createSquareCanvasContext(1)) return originalDataUri;

  try {
    const image = await loadImageElement(originalDataUri);
    const sourceSize = Math.min(image.naturalWidth || 0, image.naturalHeight || 0);
    if (!sourceSize) return originalDataUri;

    const outputSize = Math.min(AVATAR_OUTPUT_SIZE, sourceSize);
    const surface = createSquareCanvasContext(outputSize);
    if (!surface) return originalDataUri;
    const { canvas, context } = surface;

    context.drawImage(
      image,
      Math.max(0, ((image.naturalWidth || 0) - sourceSize) / 2),
      Math.max(0, ((image.naturalHeight || 0) - sourceSize) / 2),
      sourceSize,
      sourceSize,
      0,
      0,
      outputSize,
      outputSize,
    );

    // PNG and WEBP can carry transparency, and a canvas exported as JPEG turns
    // that transparency black -- which is exactly what a logo would hit.
    const sourceType = String(file.type || "").toLowerCase();
    const keepsAlpha = sourceType === "image/png" || sourceType === "image/webp";
    const encoded = keepsAlpha
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", JPEG_QUALITY);

    return typeof encoded === "string" && encoded.startsWith("data:image/")
      ? encoded
      : originalDataUri;
  } catch (error) {
    return originalDataUri;
  }
};
